// ONE settle detector: `scrollend` where it exists, a debounce where not, both agreeing about a held finger.

// Axis-agnostic: the stack passes scrollLeft, the sheet scrollTop.
export function snapProgress(scrolled: number, maxScroll: number): number {
  if (maxScroll <= 0) return 0
  const progress = 1 - scrolled / maxScroll
  return progress < 0 ? 0 : progress > 1 ? 1 : progress
}

// "At the end", with a tolerance: a scroll that has reached the far slot reads 0.98-1, never exactly 1, under a momentum tail.
export const SNAP_LANDED = 0.98

/** How long after the last scroll event a sequence counts as settled, where `scrollend` isn't implemented. */
export const SNAP_SETTLE_DEBOUNCE_MS = 140

// A scroller that never reaches a slot would otherwise re-arm the debounce forever.
const SNAP_RETRY_LIMIT = 20

/**
 * A touch's later events go to the element it STARTED on, and one React replaced under the finger bubbles to nothing.
 * This hands them to the handlers from the detached target itself (never twice for a connected one). Returns the detach.
 */
export function listenForDetachedTouch(
  event: TouchEvent,
  handlers: { onMove?: (event: TouchEvent) => void; onEnd: (event: TouchEvent) => void },
): () => void {
  const target = event.target
  if (!(target instanceof Node)) return () => {}
  const onMove = (move: Event) => {
    if (!target.isConnected) handlers.onMove?.(move as TouchEvent)
  }
  const onEnd = (end: Event) => {
    if (!target.isConnected) handlers.onEnd(end as TouchEvent)
  }
  if (handlers.onMove) target.addEventListener('touchmove', onMove, { passive: true })
  target.addEventListener('touchend', onEnd, { passive: true })
  target.addEventListener('touchcancel', onEnd, { passive: true })
  return () => {
    target.removeEventListener('touchmove', onMove)
    target.removeEventListener('touchend', onEnd)
    target.removeEventListener('touchcancel', onEnd)
  }
}

export interface SnapScrollerHandlers {
  // Tell a position the surface caused itself apart from a gesture by POSITION, never by a timer.
  owns?: () => boolean
  /** The first frame of a scroll the gesture owns — where the surface switches into its dragging state. */
  onStart?: () => void
  /** Every rAF while it moves. Keep this to READS plus cheap writes; it is the frame path. */
  onFrame: () => void
  // Settled means POSITION, not silence: on the debounce path a long task freezes a snap animation and its scroll
  // events together, so silence can mean the opposite of settled. Anything but `false` is taken as arrived.
  onSettle: (wasScrolling: boolean) => boolean | void
  /**
   * For a surface that owns `scroll-snap-type` outright: the observer calls this where it would have turned snap off
   * and never touches the style, since it restores on the WINDOW's touchend, after the surface's own release.
   */
  suppressSnap?: () => void
}

/** Live state of an observed scroller, for the surfaces that need to ask rather than track it themselves. */
export interface SnapScrollerHandle {
  /** Stop observing. */
  stop: () => void
  /** Is a finger down on this scroller right now? */
  held: () => boolean
  // Spends the sequence for a consumer that acted before the settle, so the late announcement is not a second
  // landing and the next drag still gets its onStart.
  consume: () => void
}

// Coalesced to one onFrame per animation frame, so a starved frame lags a derived cue rather than stalling the gesture.
export function observeSnapScroller(el: HTMLElement, handlers: SnapScrollerHandlers): SnapScrollerHandle {
  let frame = 0
  let settleTimer = 0
  let scrolling = false
  let retries = 0
  // Nothing settles while a finger is down (a stalled drag, or a finger outside the box). TOUCH, not pointer: the
  // browser fires pointercancel the instant a native scroll claims the gesture.
  let held = false
  /** A settle that arrived while the finger was down. Owed to the release, which re-arms the debounce for it. */
  let owed = false
  // A settle under a held finger is the browser's own snap-back, which the finger yanks against: snap goes off until
  // the touch ends, and restoring it IS the snap-back.
  let snapSuppressed = false

  const suppressSnap = () => {
    if (snapSuppressed) return
    snapSuppressed = true
    if (handlers.suppressSnap) handlers.suppressSnap()
    else el.style.scrollSnapType = 'none'
  }

  const restoreSnap = () => {
    if (!snapSuppressed) return
    snapSuppressed = false
    if (!handlers.suppressSnap) el.style.scrollSnapType = ''
  }
  // Also the backstop, on `scrollend` engines, for a settle refused under a held finger.
  const supportsScrollEnd = 'onscrollend' in window

  const publish = () => {
    frame = 0
    handlers.onFrame()
  }

  const armDebounce = () => {
    clearTimeout(settleTimer)
    settleTimer = window.setTimeout(settle, SNAP_SETTLE_DEBOUNCE_MS)
  }

  function settle() {
    clearTimeout(settleTimer)
    settleTimer = 0
    if (held) {
      owed = true
      suppressSnap()
      return
    }
    owed = false
    const was = scrolling
    scrolling = false
    if (handlers.onSettle(was) === false && was && ++retries < SNAP_RETRY_LIMIT) {
      // `scrollend` will not fire twice for one scroll, so this path is the debounce's alone.
      scrolling = true
      armDebounce()
      return
    }
    retries = 0
  }

  const onScroll = () => {
    if (handlers.owns && !handlers.owns()) return
    if (!scrolling) {
      scrolling = true
      retries = 0
      handlers.onStart?.()
    }
    if (!frame) frame = requestAnimationFrame(publish)
    // Re-armed every frame while it is the deciding signal, so it can only fire once the scroller has stopped.
    if (!supportsScrollEnd || owed) armDebounce()
  }

  let detachTouch: (() => void) | null = null
  const onTouchStart = (event: TouchEvent) => {
    detachTouch?.()
    detachTouch = listenForDetachedTouch(event, { onEnd: onTouchEnd })
    held = true
    owed = false
    // A fresh gesture supersedes whatever the last one was still counting down to.
    clearTimeout(settleTimer)
    settleTimer = 0
  }

  const onTouchEnd = (event: TouchEvent) => {
    if (event.touches.length > 0) return // another finger is still down; the gesture isn't over
    detachTouch?.()
    if (!held) return
    held = false
    // Before the debounce is armed: the restyle starts the browser's snap-back, whose events then re-arm that clock.
    restoreSnap()
    // The release is where momentum and snap BEGIN, so a timer armed here would read a half-finished position.
    if (owed) armDebounce()
  }

  el.addEventListener('scroll', onScroll, { passive: true })
  el.addEventListener('touchstart', onTouchStart, { passive: true })
  window.addEventListener('touchend', onTouchEnd, { passive: true })
  window.addEventListener('touchcancel', onTouchEnd, { passive: true })
  if (supportsScrollEnd) el.addEventListener('scrollend', settle)

  return {
    held: () => held,
    consume: () => {
      clearTimeout(settleTimer)
      settleTimer = 0
      owed = false
      scrolling = false
      retries = 0
    },
    stop: () => {
      if (frame) cancelAnimationFrame(frame)
      clearTimeout(settleTimer)
      detachTouch?.()
      restoreSnap()
      el.removeEventListener('scroll', onScroll)
      el.removeEventListener('touchstart', onTouchStart)
      window.removeEventListener('touchend', onTouchEnd)
      window.removeEventListener('touchcancel', onTouchEnd)
      if (supportsScrollEnd) el.removeEventListener('scrollend', settle)
    },
  }
}
